
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageTransition from '@/components/PageTransition';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { generateIndustryServiceSchema } from '@/utils/seoSchema';
import { updateMetaTags, getCanonicalUrl } from '@/utils/seoUtils';
import HeroSection from '@/components/social-media/HeroSection';
import WhyKIVideos from '@/components/social-media/WhyKIVideos';
import ProcessSteps from '@/components/social-media/ProcessSteps';
import ExampleGallery from '@/components/social-media/ExampleGallery';
import FAQSection from '@/components/social-media/FAQSection';
import CtaSection from '@/components/social-media/CtaSection';
import FloatingCTA from '@/components/social-media/FloatingCTA';

const LuxuryIndustry = () => {
  const { lang, city } = useParams();
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];
  
  useEffect(() => {
    // SEO meta tags setup based on language
    const titlesByLang = {
      de: city ? `KI-Videomarketing für Luxusmarken ${city} | Future Level` : "KI-Videomarketing für Luxusmarken | Future Level",
      en: city ? `AI Video Marketing for Luxury Brands ${city} | Future Level` : "AI Video Marketing for Luxury Brands | Future Level",
      fr: city ? `Marketing Vidéo IA pour Marques de Luxe ${city} | Future Level` : "Marketing Vidéo IA pour Marques de Luxe | Future Level",
      it: city ? `Marketing Video AI per Marchi di Lusso ${city} | Future Level` : "Marketing Video AI per Marchi di Lusso | Future Level"
    };
    
    const descriptionsByLang = {
      de: city ? 
        `Premium KI-Videoinhalte für Luxusmarken und hochwertige Produkte in ${city}. Exklusiv für Schweizer Premiummarken mit Fokus auf Eleganz und visuelle Qualität.` : 
        "Premium KI-Videoinhalte für Luxusmarken und hochwertige Produkte. Exklusiv für Schweizer Premiummarken mit Fokus auf Eleganz und visuelle Qualität.",
      en: city ?
        `Premium AI video content for luxury brands and high-end products in ${city}. Exclusively for Swiss premium brands focusing on elegance and visual quality.` :
        "Premium AI video content for luxury brands and high-end products. Exclusively for Swiss premium brands focusing on elegance and visual quality.",
      fr: city ?
        `Contenu vidéo IA premium pour marques de luxe et produits haut de gamme à ${city}. Exclusivement pour les marques premium suisses axées sur l'élégance et la qualité visuelle.` :
        "Contenu vidéo IA premium pour marques de luxe et produits haut de gamme. Exclusivement pour les marques premium suisses axées sur l'élégance et la qualité visuelle.",
      it: city ?
        `Contenuto video AI premium per marchi di lusso e prodotti di alta gamma a ${city}. Esclusivamente per marchi premium svizzeri con focus su eleganza e qualità visiva.` :
        "Contenuto video AI premium per marchi di lusso e prodotti di alta gamma. Esclusivamente per marchi premium svizzeri con focus su eleganza e qualità visiva."
    };
    
    const keywordsByLang = {
      de: city ? 
        `Luxusmarken KI-Videos ${city}, Premium Produktvideos ${city}, Hochwertiges Videomarketing ${city}, KI-Videos Schweizer Luxusmarken, Luxus Marketing AI` :
        "Luxusmarken KI-Videos, Premium Produktvideos, Hochwertiges Videomarketing, KI-Videos Schweizer Luxusmarken, Luxus Marketing AI",
      en: city ?
        `Luxury brands AI videos ${city}, premium product videos ${city}, high-end video marketing ${city}, AI videos Swiss luxury brands, luxury marketing AI` :
        "Luxury brands AI videos, premium product videos, high-end video marketing, AI videos Swiss luxury brands, luxury marketing AI",
      fr: city ?
        `Vidéos IA marques de luxe ${city}, vidéos produits premium ${city}, marketing vidéo haut de gamme ${city}, vidéos IA marques de luxe suisses, marketing de luxe IA` :
        "Vidéos IA marques de luxe, vidéos produits premium, marketing vidéo haut de gamme, vidéos IA marques de luxe suisses, marketing de luxe IA",
      it: city ?
        `Video AI marchi di lusso ${city}, video prodotti premium ${city}, marketing video alta gamma ${city}, video AI marchi di lusso svizzeri, marketing lusso AI` :
        "Video AI marchi di lusso, video prodotti premium, marketing video alta gamma, video AI marchi di lusso svizzeri, marketing lusso AI"
    };
    
    // Determine the canonical URL path based on language
    const pagePath = currentLanguage === 'de' ? '/leistungen/ki-videomarketing-luxusmarken' : 
                    currentLanguage === 'en' ? '/services/ai-video-marketing-luxury' : 
                    currentLanguage === 'fr' ? '/services/video-marketing-ia-luxe' : 
                    '/servizi/marketing-video-ia-lusso';
                    
    const canonicalUrl = getCanonicalUrl(`${pagePath}${city ? `/${city.toLowerCase()}` : ''}`, currentLanguage);
    
    // Update meta tags for SEO
    updateMetaTags({
      title: titlesByLang[currentLanguage as keyof typeof titlesByLang],
      description: descriptionsByLang[currentLanguage as keyof typeof descriptionsByLang],
      keywords: keywordsByLang[currentLanguage as keyof typeof keywordsByLang],
      canonicalUrl: canonicalUrl,
      language: currentLanguage,
      ogType: "website"
    });
    
    // Add structured data with industry-specific schema
    generateIndustryServiceSchema(
      'luxury',
      titlesByLang[currentLanguage as keyof typeof titlesByLang],
      descriptionsByLang[currentLanguage as keyof typeof descriptionsByLang],
      "https://future-level.ch/opengraph-image-p98pqg.png",
      canonicalUrl,
      currentLanguage,
      city
    );
    
  }, [currentLanguage, city]);
  
  // Industry-specific content for luxury
  const getHeroContent = () => {
    const content = {
      de: {
        title: city ? `KI-Videomarketing für Luxusmarken in ${city}` : 'KI-Videomarketing für Luxusmarken',
        subtitle: 'Exklusive KI-generierte Video-Inhalte für Premium-Marken und hochwertige Produkte',
        description: 'Erzählen Sie die Geschichte Ihrer Luxusmarke mit eleganten und visuell ansprechenden KI-Videos. Präsentieren Sie Ihre Premium-Produkte auf innovative Weise und schaffen Sie ein unvergessliches Markenerlebnis.',
      },
      en: {
        title: city ? `AI Video Marketing for Luxury Brands in ${city}` : 'AI Video Marketing for Luxury Brands',
        subtitle: 'Exclusive AI-generated video content for premium brands and high-end products',
        description: "Tell your luxury brand's story with elegant and visually appealing AI videos. Present your premium products in innovative ways and create an unforgettable brand experience.",
      },
      fr: {
        title: city ? `Marketing Vidéo IA pour Marques de Luxe à ${city}` : 'Marketing Vidéo IA pour Marques de Luxe',
        subtitle: 'Contenu vidéo exclusif généré par IA pour marques premium et produits haut de gamme',
        description: 'Racontez l\'histoire de votre marque de luxe avec des vidéos IA élégantes et visuellement attrayantes. Présentez vos produits premium de manière innovante et créez une expérience de marque inoubliable.',
      },
      it: {
        title: city ? `Marketing Video AI per Marchi di Lusso a ${city}` : 'Marketing Video AI per Marchi di Lusso',
        subtitle: 'Contenuti video esclusivi generati da AI per marchi premium e prodotti di alta gamma',
        description: 'Racconta la storia del tuo marchio di lusso con video AI eleganti e visivamente accattivanti. Presenta i tuoi prodotti premium in modi innovativi e crea un\'esperienza di marca indimenticabile.',
      }
    };
    
    return content[currentLanguage as keyof typeof content] || content.en;
  };
  
  const heroContent = getHeroContent();
  
  return (
    <div className="flex flex-col min-h-screen bg-[#121212]">
      <Header />
      <PageTransition>
        <main className="flex-grow pt-24">
          <HeroSection 
            title={heroContent.title} 
            subtitle={heroContent.subtitle} 
            description={heroContent.description}
            industry="luxury"
          />
          
          <WhyKIVideos industry="luxury" />
          
          <ProcessSteps industry="luxury" />
          
          {/* Premium Solutions section */}
          <section className="py-16 px-4 md:px-8 bg-gradient-to-br from-purple-900/20 to-black">
            <div className="container mx-auto max-w-6xl">
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white text-center">
                {currentLanguage === 'de' ? 'Premium-Lösungen für Luxusmarken' :
                 currentLanguage === 'en' ? 'Premium Solutions for Luxury Brands' :
                 currentLanguage === 'fr' ? 'Solutions Premium pour Marques de Luxe' :
                 'Soluzioni Premium per Marchi di Lusso'}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
                <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 hover:border-purple-500/50 transition-all">
                  <h3 className="text-xl font-semibold text-white mb-4">
                    {currentLanguage === 'de' ? 'Elegante Produktpräsentationen' :
                     currentLanguage === 'en' ? 'Elegant Product Presentations' :
                     currentLanguage === 'fr' ? 'Présentations de Produits Élégantes' :
                     'Presentazioni Eleganti dei Prodotti'}
                  </h3>
                  <p className="text-gray-300">
                    {currentLanguage === 'de' ? 'Maßgeschneiderte Videos, die die Exklusivität und Handwerkskunst Ihrer Premium-Produkte hervorheben.' :
                     currentLanguage === 'en' ? 'Custom videos highlighting the exclusivity and craftsmanship of your premium products.' :
                     currentLanguage === 'fr' ? 'Vidéos sur mesure mettant en valeur l\'exclusivité et le savoir-faire de vos produits premium.' :
                     'Video personalizzati che evidenziano l\'esclusività e l\'artigianato dei tuoi prodotti premium.'}
                  </p>
                </div>
                
                <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 hover:border-purple-500/50 transition-all">
                  <h3 className="text-xl font-semibold text-white mb-4">
                    {currentLanguage === 'de' ? 'Brand Storytelling' :
                     currentLanguage === 'en' ? 'Brand Storytelling' :
                     currentLanguage === 'fr' ? 'Narration de Marque' :
                     'Storytelling del Brand'}
                  </h3>
                  <p className="text-gray-300">
                    {currentLanguage === 'de' ? 'Erzählen Sie die Geschichte und Tradition Ihrer Luxusmarke mit visuell beeindruckenden Inhalten.' :
                     currentLanguage === 'en' ? 'Tell the story and tradition of your luxury brand with visually impressive content.' :
                     currentLanguage === 'fr' ? 'Racontez l\'histoire et la tradition de votre marque de luxe avec un contenu visuellement impressionnant.' :
                     'Racconta la storia e la tradizione del tuo marchio di lusso con contenuti visivamente impressionanti.'}
                  </p>
                </div>
                
                <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 hover:border-purple-500/50 transition-all">
                  <h3 className="text-xl font-semibold text-white mb-4">
                    {currentLanguage === 'de' ? 'Exklusive Markenerlebnisse' :
                     currentLanguage === 'en' ? 'Exclusive Brand Experiences' :
                     currentLanguage === 'fr' ? 'Expériences de Marque Exclusives' :
                     'Esperienze di Marca Esclusive'}
                  </h3>
                  <p className="text-gray-300">
                    {currentLanguage === 'de' ? 'Schaffen Sie immersive visuelle Erlebnisse, die die Einzigartigkeit Ihrer Luxusmarke unterstreichen.' :
                     currentLanguage === 'en' ? 'Create immersive visual experiences that emphasize the uniqueness of your luxury brand.' :
                     currentLanguage === 'fr' ? 'Créez des expériences visuelles immersives qui soulignent l\'unicité de votre marque de luxe.' :
                     'Crea esperienze visive immersive che sottolineano l\'unicità del tuo marchio di lusso.'}
                  </p>
                </div>
              </div>
            </div>
          </section>
          
          <ExampleGallery industry="luxury" />
          
          <FAQSection industry="luxury" />
          
          <CtaSection industry="luxury" />
        </main>
      </PageTransition>
      <Footer />
      <FloatingCTA />
    </div>
  );
};

export default LuxuryIndustry;
